import React, { useContext, useEffect, useRef, useState } from 'react';
import { PageContentBodyGrid } from '../../Shared/Page/Page';
import { FileText, Play, Upload, RefreshCw, Trash2, Maximize2 } from 'react-feather';
import { format } from 'date-fns';
import { RawEditableText } from '../../../contents/EditableText';
import { PortfolioManagerContext } from '../PortfolioManagement/PortfolioManagerPage';
import { FundContext } from './FundPage';
import WebfontLoader from '@dr-kobros/react-webfont-loader';
import PageGridItem from '../../Shared/Page/PageGridItem';
import DetailSectionHeader from '../../Shared/DetailSection/DetailSectionHeader';
import DetailField from '../../Shared/DetailSection/DetailField';
import DetailTextbox from './DetailTextbox';
import BooleanDetailField from '../../Shared/DetailSection/BooleanDetailField';
import RiskIndicator from './RiskIndicator';
import LoadingIcon from '../../Shared/LoadingIcon';
import Modal from '../../../components/Modals/Modal';
import { useRemote } from '../../../Utils/Utils';
import Tooltip from '../../../components/popups/Tooltip';
import { toastWarning } from '../../../components/popups/Toast';
import { uploadVideoToBunny, validateVideoFile, ACCEPTED_VIDEO_ACCEPT_ATTR } from '../../../Utils/bunnyUpload';
import { buildVideoTitle } from '../../../Utils/videoTitle';
import "./DynamicDetails.scss";
import ImageUploadControl from '../../../components/ImageUploadControl';


const dynamicConfig = {
	custom: {
		families: [
			"Newsreader",
			"Roboto",
			"Roboto Bold",
			"Roboto Condensed",
			"Roboto Condensed Bold",
			"Roboto Condensed Light",
			"Roboto Condensed Light Italic",
			"Roboto Condensed Italic",
			"Roboto Medium",
			"Roboto Light",
			"Roboto Light Italic",
			"Gotham Book",
			"Gotham Bold",
			"Gotham Book Italic",
			"Gotham Bold Italic",
			"Gotham Light Italic",
			"Gotham Medium",
			"Gotham Medium Italic",
			"Gotham Light"
		],
		urls: [
			"/api/fonts/Newsreader.css",
			"/api/fonts/Roboto.css",
			"/api/fonts/Roboto Bold.css",
			"/api/fonts/Roboto Condensed.css",
			"/api/fonts/Roboto Condensed Bold.css",
			"/api/fonts/Roboto Condensed Light.css",
			"/api/fonts/Roboto Condensed Light Italic.css",
			"/api/fonts/Roboto Condensed Italic.css",
			"/api/fonts/Roboto Medium.css",
			"/api/fonts/Roboto Light.css",
			"/api/fonts/Roboto Light Italic.css",
			"/api/fonts/Gotham Book.css",
			"/api/fonts/Gotham Bold.css",
			"/api/fonts/Gotham Book Italic.css",
			"/api/fonts/Gotham Bold Italic.css",
			"/api/fonts/Gotham Light Italic.css",
			"/api/fonts/Gotham Medium.css",
			"/api/fonts/Gotham Medium Italic.css",
			"/api/fonts/Gotham Light.css"
		]
	}
}

const slateMockStyles = {
	"!preview-watermark": {
		"fontFamily": "Roboto Medium",
		"color": "#060871",
		"size": 8
	},
	"title-page-header": {
		"color": "#FFFFFF",
		"fontFamily": "Newsreader",
		"size": 20,
		"lineHeight": 21
	},
	"title-page-subheader": {
		"fontFamily": "Roboto Light",
		"size": 16,
		"color": "#FFFFFF"
	},
	"page-header": {
		"fontFamily": "Roboto Light",
		"color": "#3397D2",
		"size": 22,
		"lineHeight": 20
	},
	"title-page-date": {
		"color": "#000119",
		"size": 8,
		"fontFamily": "Roboto Medium"
	},
	"title-page-text": {
		"fontFamily": "Roboto Light",
		"color": "#0D0D0D",
		"size": 11.43
	},
	"footer-label": {
		"fontFamily": "Roboto Light",
		"color": "#3397D2",
		"size": 7.68,
		"textAlign": "right"
	},
	"body-li": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"textAlign": "justify",
		"paddingBelowParagraph": 4,
		"indent": 20
	},
	"body-header": {
		"fontFamily": "Roboto Light",
		"color": "#0761A2",
		"size": 9,
		"paddingBelowParagraph": 6
	},
	"body-text-placeholder": {
		"fontFamily": "Roboto Light",
		"color": "#C6C6C6",
		"size": 8,
		"paddingBelowParagraph": 0,
		"lineHeight": 9
	},
	"body-text": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"paddingBelowParagraph": 4,
		"lineHeight": 9
	},
	"body-text-large": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 10,
		"paddingBelowParagraph": 0,
		"lineHeight": 11
	},
	"body-text-bold": {
		"fontFamily": "Roboto",
		"color": "#000000",
		"fontWeight": "600",
		"size": 8,
		"paddingBelowParagraph": 0,
		"lineHeight": 9
	},
	"body-text-small": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8.18,
		"paddingBelowParagraph": 0,
		"lineHeight": 12
	},
	"table-header": {
		"fontFamily": "Roboto Light",
		"color": "#224C95",
		"size": 12.18
	},
	"table-header-row": {
		"fontFamily": "Roboto Medium",
		"color": "#FFFFFF",
		"backgroundColor": "#030335",
		"size": 8,
		"borderBottomWidth": 0,
		"borderBottomColor": "none",
		"borderBottomStyle": "none",
		"paddingLeft": 4.5,
		"paddingRight": 4.5,
		"paddingTop": 4.5,
		"paddingBottom": 4.5
	},
	"table-header-row-small": {
		"fontFamily": "Roboto Light",
		"color": "#FFFFFF",
		"backgroundColor": "#030335",
		"size": 8,
		"borderBottomWidth": 0,
		"borderBottomColor": "none",
		"borderBottomStyle": "none",
		"paddingLeft": 4.5,
		"paddingRight": 4.5,
		"paddingTop": 4.5,
		"paddingBottom": 4.5
	},
	"table-highlight-row": {
		"fontFamily": "Roboto Medium",
		"color": "#000119",
		"backgroundColor": "#F1F0F5",
		"size": 8,
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-highlight-row-bold": {
		"fontFamily": "Roboto Bold",
		"color": "#000119",
		"backgroundColor": "#F1F0F5",
		"size": 8,
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-highlight-col": {
		"fontFamily": "Roboto Medium",
		"color": "#000119",
		"backgroundColor": "#F1F0F5",
		"size": 8,
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell-growth": {
		"fontFamily": "Roboto",
		"color": "#FFFFFF",
		"size": 8,
		"backgroundColor": "#7A2E8F",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-aus-eq": {
		"fontFamily": "Roboto Light",
		"color": "#FFFFFF",
		"size": 8,
		"backgroundColor": "#9458A3",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-int-eq": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#BC96C5",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-property": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#DECBE3",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-infrastructyre": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#F1E9F4",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-defensive": {
		"fontFamily": "Roboto",
		"color": "#FFFFFF",
		"size": 8,
		"backgroundColor": "#154A9A",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-alternatives": {
		"fontFamily": "Roboto Light",
		"color": "#FFFFFF",
		"size": 8,
		"backgroundColor": "#466FAF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-aus-fi": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#8BA4CC",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-int-fi": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#C6D2E6",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-cash": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#E8EDF5",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-bold": {
		"fontFamily": "Roboto Medium",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell-no-bg": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "none",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell-header-text": {
		"fontFamily": "Roboto Medium",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#F1F0F5",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell-tall": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"lineHeight": 1.2,
		"paddingLeft": 7,
		"paddingRight": 3.75,
		"paddingTop": 7,
		"paddingBottom": 7
	},
	"table-cell-header-text-tall": {
		"fontFamily": "Roboto Medium",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#F1F0F5",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 7,
		"paddingRight": 3.75,
		"paddingTop": 7,
		"paddingBottom": 7
	},
	"table-total-row": {
		"fontFamily": "Roboto Medium",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#001E41",
		"borderBottomStyle": "solid",
		"borderTopWidth": 0.5,
		"borderTopColor": "#001E41",
		"borderTopStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-footer": {
		"fontFamily": "Roboto Condensed Light",
		"color": "#000000",
		"size": 8,
		"lineHeight": 9
	},
	"page-footer": {
		"fontFamily": "Roboto Light",
		"color": "#FFFFFF",
		"textAlign": "left",
		"size": 10,
		"lineHeight": 6.5
	},
	"page-footer-bold": {
		"fontFamily": "Roboto",
		"color": "#FFFFFF",
		"textAlign": "left",
		"size": 10,
		"lineHeight": 8.5
	},
	"page-number": {
		"fontFamily": "Roboto Light",
		"color": "#FFFFFF",
		"size": 10,
		"lineHeight": 6.5
	},
	"section-header": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 12.93
	},
	"section-header-blue": {
		"fontFamily": "Roboto Light",
		"color": "#164A9A",
		"size": 14,
		"paddingBelowParagraph": 4
	},
	"section-header-blue-medium": {
		"fontFamily": "Roboto Light",
		"color": "#164A9A",
		"size": 10,
		"paddingBelowParagraph": 0
	},
	"section-header-blue-small": {
		"fontFamily": "Roboto Light",
		"color": "#164A9A",
		"size": 8,
		"paddingBelowParagraph": 0
	},
	"sidebar-table-header": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 12.93
	},
	"sidebar-table-cell": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 1,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3,
		"paddingRight": 3,
		"paddingTop": 3.75,
		"paddingBottom": 3.75,
		"verticalAlign": "top"
	},
	"sidebar-table-cell-left": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#E9EDF4",
		"borderBottomWidth": 1,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3,
		"paddingRight": 1,
		"paddingTop": 3.75,
		"paddingBottom": 3.75,
		"verticalAlign": "top"
	},
	"disclaimer-text": {
		"fontFamily": "Roboto Condensed Light",
		"color": "#000119",
		"size": 8,
		"paddingBelowParagraph": 0,
		"lineHeight": 8.5
	},
	"disclaimer-text-bold": {
		"fontFamily": "Roboto Condensed",
		"color": "#000119",
		"size": 8,
		"paddingBelowParagraph": 0,
		"lineHeight": 8.5
	},
	"sidebar-table-cell-header": {
		"fontFamily": "Roboto Light",
		"color": "#000000",
		"size": 8,
		"backgroundColor": "#E9EDF4",
		"borderBottomWidth": 1,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3,
		"paddingRight": 1,
		"paddingTop": 3.75,
		"paddingBottom": 3.75,
		"verticalAlign": "top"
	},
}

export const slateMock3SkyeStyle = {
	"!preview-watermark": {
		"fontFamily": "Metropolis",
		"fontWeight": 500,
		"color": "#060871",
		"size": 8
	},
	"title-page-header": {
		"color": "#FFFFFF",
		"fontFamily": "Newsreader",
		"size": 20,
		"lineHeight": 21
	},
	"title-page-subheader": {
		"fontFamily": "Metropolis",
		"size": 16,
		"color": "#FFFFFF"
	},
	"page-header": {
		"fontFamily": "Metropolis",
		"color": "#3397D2",
		"size": 22,
		"lineHeight": 20
	},
	"title-page-date": {
		"color": "#000119",
		"size": 8,
		"fontFamily": "Metropolis",
		"fontWeight": 500,
	},
	"title-page-text": {
		"fontFamily": "Metropolis",
		"color": "#0D0D0D",
		"size": 11.43
	},
	"footer-label": {
		"fontFamily": "Metropolis",
		"color": "#3397D2",
		"size": 7.68,
		"textAlign": "right"
	},
	"body-li": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 8,
		"textAlign": "justify",
		"paddingBelowParagraph": 4,
		"indent": 20
	},
	"body-header": {
		"fontFamily": "Metropolis",
		"color": "#0E8EFD",
		"size": 9,
		"paddingBelowParagraph": 6
	},
	"body-text-placeholder": {
		"fontFamily": "Metropolis",
		"color": "#C6C6C6",
		"size": 8,
		"paddingBelowParagraph": 0,
		"lineHeight": 9
	},
	"body-text": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"paddingBelowParagraph": 4,
		"lineHeight": 12
	},
	"body-text-large": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 10,
		"paddingBelowParagraph": 0,
		"lineHeight": 11
	},
	"body-text-bold": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"fontWeight": "600",
		"size": 12,
		"paddingBelowParagraph": 0,
		"lineHeight": 14
	},
	"body-text-small": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 10,
		"paddingBelowParagraph": 0,
		"lineHeight": 12
	},
	"table-header": {
		"fontFamily": "Metropolis",
		"color": "#0E8EFD",
		"size": 15
	},
	"table-header-row": {
		"fontFamily": "Metropolis",
		"fontWeight": 500,
		"color": "#FFFFFF",
		"backgroundColor": "#030335",
		"size": 12,
		"borderBottomWidth": 0,
		"borderBottomColor": "none",
		"borderBottomStyle": "none",
		"paddingLeft": 4.5,
		"paddingRight": 4.5,
		"paddingTop": 4.5,
		"paddingBottom": 4.5
	},
	"table-header-row-small": {
		"fontFamily": "Metropolis",
		"color": "#FFFFFF",
		"backgroundColor": "#030335",
		"size": 12,
		"borderBottomWidth": 0,
		"borderBottomColor": "none",
		"borderBottomStyle": "none",
		"paddingLeft": 4.5,
		"paddingRight": 4.5,
		"paddingTop": 4.5,
		"paddingBottom": 4.5
	},
	"table-highlight-row": {
		"fontFamily": "Metropolis",
		"fontWeight": 500,
		"color": "#000119",
		"backgroundColor": "#F1F0F5",
		"size": 12,
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-highlight-row-bold": {
		"fontFamily": "Metropolis",
		"fontWeight": 600,
		"color": "#000119",
		"backgroundColor": "#F1F0F5",
		"size": 12,
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-highlight-col": {
		"fontFamily": "Metropolis",
		"fontWeight": 500,
		"color": "#000119",
		"backgroundColor": "#F1F0F5",
		"size": 12,
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell-growth": {
		"fontFamily": "Roboto",
		"color": "#FFFFFF",
		"size": 12,
		"backgroundColor": "#7A2E8F",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-aus-eq": {
		"fontFamily": "Metropolis",
		"color": "#FFFFFF",
		"size": 12,
		"backgroundColor": "#9458A3",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-int-eq": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#BC96C5",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-property": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#DECBE3",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-infrastructyre": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#F1E9F4",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-defensive": {
		"fontFamily": "Roboto",
		"color": "#FFFFFF",
		"size": 12,
		"backgroundColor": "#154A9A",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-alternatives": {
		"fontFamily": "Metropolis",
		"color": "#FFFFFF",
		"size": 12,
		"backgroundColor": "#466FAF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-aus-fi": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#8BA4CC",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-int-fi": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#C6D2E6",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-cash": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#E8EDF5",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#FFFFFF",
		"borderBottomStyle": "solid",
		"paddingLeft": 8.75,
		"paddingRight": 3.75,
		"paddingTop": 5,
		"paddingBottom": 5
	},
	"table-cell-bold": {
		"fontFamily": "Metropolis",
		"fontWeight": 500,
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell-no-bg": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "none",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell-header-text": {
		"fontFamily": "Metropolis",
		"fontWeight": 500,
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#F1F0F5",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-cell-tall": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"lineHeight": 1.2,
		"paddingLeft": 7,
		"paddingRight": 3.75,
		"paddingTop": 7,
		"paddingBottom": 7
	},
	"table-cell-header-text-tall": {
		"fontFamily": "Metropolis",
		"fontWeight": 500,
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#F1F0F5",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 7,
		"paddingRight": 3.75,
		"paddingTop": 7,
		"paddingBottom": 7
	},
	"table-total-row": {
		"fontFamily": "Metropolis",
		"fontWeight": 500,
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 0.5,
		"borderBottomColor": "#001E41",
		"borderBottomStyle": "solid",
		"borderTopWidth": 0.5,
		"borderTopColor": "#001E41",
		"borderTopStyle": "solid",
		"paddingLeft": 3.75,
		"paddingRight": 3.75,
		"paddingTop": 3,
		"paddingBottom": 3
	},
	"table-footer": {
		"fontFamily": "Roboto Condensed Light",
		"color": "#000000",
		"size": 12,
		"lineHeight": 9
	},
	"page-footer": {
		"fontFamily": "Metropolis",
		"color": "#FFFFFF",
		"textAlign": "left",
		"size": 10,
		"lineHeight": 6.5
	},
	"page-footer-bold": {
		"fontFamily": "Metropolis",
		"color": "#FFFFFF",
		"textAlign": "left",
		"size": 10,
		"lineHeight": 11
	},
	"page-number": {
		"fontFamily": "Metropolis",
		"color": "#FFFFFF",
		"size": 10,
		"lineHeight": 11
	},
	"section-header": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 23
	},
	"section-header-blue": {
		"fontFamily": "Metropolis",
		"color": "#0E8EFD",
		"size": 23,
		"paddingBelowParagraph": 6
	},
	"section-header-blue-medium": {
		"fontFamily": "Metropolis",
		"color": "#0E8EFD",
		"size": 19,
		"paddingBelowParagraph": 6
	},
	"section-header-blue-small": {
		"fontFamily": "Metropolis",
		"color": "#0E8EFD",
		"size": 15,
		"paddingBelowParagraph": 6
	},
	"sidebar-table-header": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 15
	},
	"sidebar-table-cell": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#FFFFFF",
		"borderBottomWidth": 1,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3,
		"paddingRight": 3,
		"paddingTop": 3.75,
		"paddingBottom": 3.75,
		"verticalAlign": "top"
	},
	"sidebar-table-cell-left": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#E9EDF4",
		"borderBottomWidth": 1,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3,
		"paddingRight": 1,
		"paddingTop": 3.75,
		"paddingBottom": 3.75,
		"verticalAlign": "top"
	},
	"disclaimer-text": {
		"fontFamily": "Metropolis",
		"fontWeight": 300,
		"color": "#000119",
		"size": 10,
		"paddingBelowParagraph": 0,
		"lineHeight": 11
	},
	"disclaimer-text-bold": {
		"fontFamily": "Metropolis",
		"color": "#000119",
		"size": 10,
		"paddingBelowParagraph": 0,
		"lineHeight": 11
	},
	"sidebar-table-cell-header": {
		"fontFamily": "Metropolis",
		"color": "#000000",
		"size": 12,
		"backgroundColor": "#E9EDF4",
		"borderBottomWidth": 1,
		"borderBottomColor": "#C0BFC4",
		"borderBottomStyle": "solid",
		"paddingLeft": 3,
		"paddingRight": 1,
		"paddingTop": 3.75,
		"paddingBottom": 3.75,
		"verticalAlign": "top"
	},
}



const DynamicDetails = ({ editMode, portfolio, config, staticData, updateStaticData, actualGroup, name, group, threeSkyeStyle, cancelledChanges, isClone }) => {
	const managerContext = useContext(PortfolioManagerContext)
	const fundContext = useContext(FundContext)

	// console.log("Got ", group, staticData, portfolio, config, editMode);

	const brand = managerContext?.brand || fundContext?.brand;
	if (brand) {
		//TODO setup styles properly.  For now just grab the primary colour for hyperlinks
		const primary = brand?.colours?.Primary?.value;
		if (primary) {
			slateMockStyles["brand-colour-one"] = { color: primary };
			slateMock3SkyeStyle["brand-colour-one"] = { color: primary };
		}
	}

	if (!config) {
		return null;
	}
	if (!group) {
		return null;
	}

	let latestFullwidthElementIndex = 0
	const badgedPractice = actualGroup?.properties?.badged;
	const selfLicensedPractice = actualGroup?.properties?.selfLicensed;


	return (
		<PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid style={{ overflowX: "hidden" }}>
			{name && <PageGridItem container col="1 / span 6">
				<DetailSectionHeader header={`${group.groupName === "General" ? name + " Details" : name}`} icon={<FileText />} />
			</PageGridItem>}

			{actualGroup &&
				<PageGridItem key={"heading-" + name} col={"1 / span 3"} >
					<label className="colour-text-label-grey transition-fade-up">{name} name</label>
					<div >
						{actualGroup?.groupName}
						{badgedPractice && <Tooltip label="Badged Practice"><i
							className='material-symbols-outlined'
							style={{ verticalAlign: 'middle', color: '#06467D' }}
						>license</i></Tooltip>}
						{selfLicensedPractice && <Tooltip label="Self Licensed"><i className='material-symbols-outlined' style={{ verticalAlign: 'middle', color: '#996633' }}>license</i></Tooltip>}
					</div>
				</PageGridItem >
			}

			{group.elements.map((s, index) => {

				if (!staticData) {
					return null;
				}
				const editing = editMode && (!isClone || s.rules?.core);
				const isLeftElement = s.dataType === 'fullwidth' || (index - latestFullwidthElementIndex) % 2 === 0
				const foundDatum = staticData && staticData.find(x => { return x.key === s.templateKey });
				const datum = foundDatum ? foundDatum : {}

				if (s.dataType === 'image') {
					latestFullwidthElementIndex = index + 1
					return (
						<PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col="1 / span 6">
							<label className={`${editMode ? "label-input" : "colour-text-label-grey"} mb-s`}>{s.display}</label>
							<ImageUploadControl
								key={s.templateKey}
								editMode={editing}
								schema={s}
								datum={datum}
								disabled={s.rules?.editable === false}
								onChange={(e) => { updateStaticData(s.templateKey, e) }}
							/>
							{editMode && s.description && (
								<p className='colour-text-label-grey font-size-body-s mt-s'>{s.description}</p>
							)}
						</PageGridItem>
					)
				} else if (s.dataType === 'fullwidth') {
					latestFullwidthElementIndex = index + 1
					return (
						<PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col={"1 / span 6"}>
							<DetailTextbox
								value={datum.value}
								update={(e) => updateStaticData(s.templateKey, e.target.value)}
								editMode={editing}
								placeholder={'Add ' + s.display}
								label={s.display}
								minHeight={100}
								resize='vertical'
								disabled={s.rules?.editable === false}
							/>
						</PageGridItem>
					)
				} else if (s.dataType === 'slate') {
					latestFullwidthElementIndex = index + 1
					let config = s.rules.settings["slate-options"];
					if (!datum || !datum.value || datum.value.indexOf('[') === 0) {

					} else {
						const json = [{ type: "text", children: [{ text: datum.value }] }];
						datum.value = JSON.stringify(json);
					}

					return (
						<PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col={"1 / span 6"} className={`raw-slate-detail-wrapper ${editMode ? "raw-slate-detail-wrapper-edit" : ""}`}>
							<p className={`${editMode ? "label-input" : "colour-text-label-grey"} mb-m`}>{s.display}</p>
							{cancelledChanges ? <LoadingIcon /> :
								<WebfontLoader config={dynamicConfig}>
									<RawEditableText
										config={config}
										styles={threeSkyeStyle ? slateMock3SkyeStyle : slateMockStyles}
										onChange={(e) => { updateStaticData(s.templateKey, JSON.stringify(e)) }}
										editable={editing}
										load={datum.value && JSON.parse(datum.value)}
										noEditStates
										passedClassName={threeSkyeStyle ? "no-padding-editor" : null}
										extensions={{ renderInsertMenu: true, insertTable: { show: true } }}
										lineHeight={threeSkyeStyle ? 1 : null}
										passedId="raw-slate-detail-editor"
										isClone={isClone}
							/>
								</WebfontLoader>
							}
							{editing && <p className='colour-text-label-grey font-size-body-s'>{s.description}</p>}
						</PageGridItem>
					)
				} else if (s.dataType === 'video') {
					latestFullwidthElementIndex = index + 1
					const videoOptions = s.rules?.settings?.['video-options'] || {};
					return (
						<PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col={"1 / span 6"}>
							<VideoDetail
								editMode={editMode}
								schema={s}
								datum={datum}
								options={videoOptions}
								managerName={actualGroup?.groupName}
								portfolio={portfolio}
								onChange={(val) => updateStaticData(s.templateKey, val)}
							/>
						</PageGridItem>
					)
				} else if (s?.rules?.type === 'options') {
					return (
						< PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col={isLeftElement ? "1 / span 3" : "4 / span 3"} >
							<DetailField
								dropDown={true}
								dropDownOptions={s.rules.options.map(o => { return { name: o }; })}
								editMode={editing}
								label={s.display}
								helperText={s.description}
								helperTextClass="helper-text pt-xs font-size-body-s"
								value={datum && datum.value}
								onChange={(e) => updateStaticData(s.templateKey, e)}
								disabled={s.rules?.editable === false}
								placeholder="Select an option"
								isClone={isClone}
							/>
						</PageGridItem >
					)
				} else if (s?.dataType === 'date') {
					return <PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col={isLeftElement ? "1 / span 3" : "4 / span 3"} >
						<DetailField
							editMode={editing}
							label={s.display}
							helperText={s.description}
							helperTextClass="helper-text pt-xs font-size-body-s"
							value={editing ? datum && datum.value : datum && datum.value && format(new Date(datum.value), "dd/MM/yyyy")}
							onChange={(e) => updateStaticData(s.templateKey, e.target.value)}
							disabled={s.rules?.editable === false}
							type="date"
							isClone={isClone}
						/>
					</PageGridItem >
				} else if (s?.dataType === 'boolean') {
					const { labelForFalse, labelForTrue, valueForTrue, valueForFalse } = s?.rules?.booleanOptions || {};
					// console.log("S rules: ", s.display, s.rules, labelForFalse, labelForTrue, valueForTrue, valueForFalse);

					return <PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col={isLeftElement ? "1 / span 3" : "4 / span 3"} >
						<BooleanDetailField {...s?.rules?.booleanOptions} value={datum && datum.value} label={s.display} editMode={editing} onChange={(e) => updateStaticData(s.templateKey, e)} />
					</PageGridItem>


				} else if (s?.dataType === 'numeric' && s?.rules?.type === 'risk') {
					return <PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col={isLeftElement ? "1 / span 3" : "4 / span 3"} >
						<RiskIndicator risk={+datum.value} updateFund={(num) => updateStaticData(s.templateKey, parseInt(num))} editMode={editing} />
					</PageGridItem >
				}

				return (
					< PageGridItem key={s.dataType + "-" + index} name={s.dataType + "-" + index} col={isLeftElement ? "1 / span 3" : "4 / span 3"} >
						<DetailField
							editMode={editing}
							label={s.display}
							helperText={s.description}
							helperTextClass="helper-text pt-xs font-size-body-s"
							value={datum && datum.value}
							onChange={(e) => updateStaticData(s.templateKey, e.target.value)}
							disabled={s.rules?.editable === false}
						/>
					</PageGridItem >
				)
			})}
		</PageContentBodyGrid >
	)
}



// Unwrap a StandardResponse ({ success, message, data }) down to its payload.
const unwrapResponse = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

// Older promoted rows stored a full play URL; the canonical value is now the bare
// video guid. Recover the guid either way so legacy data still renders.
function extractVideoGuid(value) {
	if (!value || typeof value !== 'string') return '';
	const trimmed = value.trim();
	if (!trimmed) return '';
	if (trimmed.includes('/')) {
		const parts = trimmed.split('/').filter(Boolean);
		return parts[parts.length - 1] || '';
	}
	return trimmed;
}

// Renders the manager video for a `dataType: video` schema element. The stored value
// is a Bunny video guid; the thumbnail and embeddable player URLs are resolved
// server-side (GET /bunny/videos/{guid}/thumbnail-url and /play-url) so the per-company
// libraryId never reaches the client. The thumbnail shows first; clicking it opens a
// modal overlay containing only the player iframe (mirrors VideoUploadPanel's player).
// Edit mode adds an editable guid field above the same thumbnail→modal preview.
// Display options (aspectRatio) come from rules.settings["video-options"], as does the
// uploaded video's naming rule — see buildVideoTitle in Utils/videoTitle.

const VideoDetail = ({ editMode, schema, datum, options, onChange, managerName, portfolio }) => {
	const remote = useRemote();
	const [thumbnailUrl, setThumbnailUrl] = useState(null);
	const [playUrl, setPlayUrl] = useState(null);
	const [thumbErrored, setThumbErrored] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [uploadState, setUploadState] = useState('idle'); // 'idle' | 'uploading' | 'error'
	const [uploadProgress, setUploadProgress] = useState(0);
	const [uploadingName, setUploadingName] = useState('');
	const [dragOver, setDragOver] = useState(false);
	const fileInputRef = useRef();
	const tusUploadRef = useRef(null);
	const guid = extractVideoGuid(datum && datum.value);
	const aspectRatio = (options && options.aspectRatio) || '16/9';

	useEffect(() => () => {
		if (tusUploadRef.current) {
			try { tusUploadRef.current.abort(); } catch (e) { /* ignore */ }
			tusUploadRef.current = null;
		}
	}, []);

	function pickFile() {
		if (fileInputRef.current) fileInputRef.current.click();
	}

	function uploadFile(file) {
		if (!file) return;
		const validationError = validateVideoFile(file);
		if (validationError) { toastWarning(validationError); return; }
		setUploadState('uploading');
		setUploadProgress(0);
		setUploadingName(file.name);
		const title = buildVideoTitle(options, { groupName: managerName, portfolio, templateKey: schema.templateKey }, file.name);
		uploadVideoToBunny(remote, file, {
			title,
			onStart: (upload) => { tusUploadRef.current = upload; },
			onProgress: (sent, total) => { if (total > 0) setUploadProgress(Math.round((sent / total) * 100)); },
		})
			.then(({ guid: uploadedGuid }) => {
				tusUploadRef.current = null;
				setUploadState('idle');
				// Store the bare guid via the component's existing onChange contract; the
				// useEffect on `guid` then resolves the thumbnail/play URLs for the preview.
				onChange(uploadedGuid);
			})
			.catch(() => {
				tusUploadRef.current = null;
				setUploadState('error');
				toastWarning('Video upload failed. Please try again.');
			});
	}

	function handleFileSelect(e) {
		const file = e.target.files[0];
		e.target.value = '';
		if (file) uploadFile(file);
	}

	function handleFileDrop(e) {
		e.preventDefault();
		setDragOver(false);
		const file = e.dataTransfer.files[0];
		if (file) uploadFile(file);
	}

	// Clear the stored Video ID. Does NOT delete the asset from Bunny storage.
	function removeVideo() {
		onChange('');
	}

	useEffect(() => {
		let cancelled = false;
		setThumbErrored(false);
		setShowModal(false);
		if (!guid) { setThumbnailUrl(null); setPlayUrl(null); return; }
		Promise.all([
			remote.get(`bunny/videos/${guid}/thumbnail-url`),
			remote.get(`bunny/videos/${guid}/play-url`),
		]).then(([thumbRes, playRes]) => {
			if (cancelled) return;
			setThumbnailUrl(unwrapResponse(thumbRes) || null);
			setPlayUrl(unwrapResponse(playRes) || null);
		}).catch(() => {
			if (cancelled) return;
			setThumbnailUrl(null);
			setPlayUrl(null);
		});
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [guid]);

	const canPlay = !!playUrl;
	const showThumbImage = thumbnailUrl && !thumbErrored;

	// The thumbnail preview button (used in both the edit-mode uploaded card and read-only mode).
	const thumbnailButton = (
		<button
			type="button"
			className="video-detail-control__preview-btn"
			onClick={() => canPlay && setShowModal(true)}
			disabled={!canPlay}
			title="Play video"
			style={{ cursor: canPlay ? 'pointer' : 'default' }}
		>
			{showThumbImage
				? <img
					src={thumbnailUrl}
					alt="Video thumbnail"
					onError={() => setThumbErrored(true)}
					style={{ display: 'block', width: '100%', aspectRatio, objectFit: 'cover' }}
				/>
				: <div style={{ width: '100%', aspectRatio, background: '#1c1c28' }} />
			}
			<span className="video-detail-control__play-badge">
				<Play size={20} color="#FFFFFF" fill="#FFFFFF" />
			</span>
			{canPlay &&
				<span className="video-detail-control__expand" aria-hidden="true"><Maximize2 size={14} /></span>
			}
		</button>
	);

	return (
		<React.Fragment>
			<p className={`${editMode ? "label-input" : "colour-text-label-grey"} mb-m`}>{schema.display}</p>

			<input type="file" accept={ACCEPTED_VIDEO_ACCEPT_ATTR} ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />

			{editMode
				? <div className="video-detail-control">
					{uploadState === 'uploading'
						? <div className="video-detail-control__uploading" aria-live="polite">
							<div className="video-detail-control__uploading-row">
								<div className="video-detail-control__uploading-tile" aria-hidden="true">
									<span className="video-detail-control__uploading-spinner" />
								</div>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div className="video-detail-control__uploading-name">{uploadingName || 'video'}</div>
									<div className="video-detail-control__uploading-status">Uploading… {uploadProgress}%</div>
								</div>
								<span className="video-detail-control__uploading-pct">{uploadProgress}%</span>
							</div>
							<div className="video-detail-control__uploading-bar" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100}>
								<div style={{ width: `${uploadProgress}%` }} />
							</div>
						</div>
						: guid
							? <div className="video-detail-control__uploaded">
								<div className="video-detail-control__preview-wrap" style={{ width: 240, maxWidth: '100%' }}>
									{thumbnailButton}
								</div>
								<div className="video-detail-control__uploaded-info">
									<div className="video-detail-control__uploaded-name" title={guid}>Video uploaded</div>
									<div className="video-detail-control__uploaded-actions">
										<Tooltip label="Replace video"><button type="button" className="video-detail-control__action-btn is-icon" onClick={pickFile} aria-label="Replace video"><RefreshCw size={15} /></button></Tooltip>
										<Tooltip label="Remove video ID"><button type="button" className="video-detail-control__action-btn is-icon is-danger" onClick={removeVideo} aria-label="Remove video ID"><Trash2 size={15} /></button></Tooltip>
									</div>
								</div>
							</div>
							: <div
								className={`video-detail-control__dropzone${dragOver ? ' is-dragging' : ''}`}
								onClick={pickFile}
								onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
								onDragLeave={() => setDragOver(false)}
								onDrop={handleFileDrop}
							>
								<span className="video-detail-control__dropzone-icon" aria-hidden="true"><Upload size={22} /></span>
								<span className="video-detail-control__dropzone-title">{dragOver ? 'Drop to upload' : 'Drag & drop a video here'}</span>
								<span className="video-detail-control__dropzone-sub">or <span className="accent">browse</span></span>
								<span className="video-detail-control__dropzone-meta">MP4, MOV, MKV, WebM, AVI… · up to 500 MB</span>
							</div>
					}

					{uploadState === 'error' &&
						<p className="colour-text-label-grey font-size-body-s mt-s">Upload failed. Please try again.</p>
					}

					<div className="video-detail-control__id-field">
						<DetailField
							editMode={editMode}
							label="Video ID"
							helperText={schema.description}
							helperTextClass="helper-text pt-xs font-size-body-s"
							value={guid}
							onChange={(e) => onChange(e.target.value)}
						/>
					</div>
				</div>
				: <div className="mt-m">
					{!guid
						? <div className="colour-text-label-grey font-size-body-s">No video</div>
						: <div className="video-detail-control__preview-wrap" style={{ width: '100%', maxWidth: 320 }}>{thumbnailButton}</div>
					}
				</div>
			}

			{showModal && playUrl &&
				<Modal large header={schema.display} handleClose={() => setShowModal(false)} overlayClick={() => setShowModal(false)}>
					<iframe
						title="Video commentary"
						src={playUrl}
						loading="lazy"
						style={{ border: 'none', width: '100%', aspectRatio }}
						allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
						allowFullScreen
					/>
				</Modal>
			}
		</React.Fragment>
	);
};

const ImageLinkDisplay = ({ editMode, portfolio, schema, datum, onChange, disabled }) => {

	const [src, setSrc] = useState(datum?.value || '');

	useEffect(() => {
		if (src?.indexOf('url:') === 0) {
			setSrc(src.substring(4))
		}
	}, [datum])


	return <React.Fragment>
		<PageGridItem col="1 / span 6">
			{editMode ? <DetailField
				editMode={editMode}
				label={schema.display}
				helperText={schema.description}
				helperTextClass="helper-text pt-xs font-size-body-s"
				value={src}
				disabled={disabled}
				onChange={(e) => { setSrc(e.target.value); onChange("url:" + e.target.value) }}
			/> : <>
				<label className="colour-text-label-grey transition-fade-up">{schema.display}</label>
				<div style={{ width: '200px', objectFit: 'contain', marginTop: '0.5rem' }}>
					<img width={200} src={src} className='dynamic-image' />
				</div>
			</>
			}
		</PageGridItem>
	</React.Fragment>
}

export default DynamicDetails;